import { useState } from 'react';
import { Button, ListBox, ListBoxItem, Select } from '@heroui/react';

import AvatarStack from '../../common/AvatarStack';
import Notice from '../../common/Notice';
import StatusChip, { TaskStatusChip, TASK_STATUS_APPEARANCE } from '../../common/StatusChip';
import { isAssignee, mayProgress, TASK_STATUS_KEYS } from '../../../services/taskAccess';
import { firstWords } from '../../../services/text';
import { TASKS, SELECT_PLACEHOLDER } from '../../../i18n/el';

/**
 * Πόσες λέξεις της περιγραφής δείχνει η **κάρτα** ενός καθηκόντος.
 *
 * Λιγότερες από της συλλογής (150): εκεί η κάρτα είναι ένα από δώδεκα στοιχεία
 * μιας επισκόπησης και η περιγραφή είναι το κύριο περιεχόμενό της· εδώ η κάρτα
 * ζει σε στήλη δίπλα σε άλλες δύο, και το ύψος της είναι ο χώρος που στερεί από
 * τα υπόλοιπα καθήκοντα της ίδιας στήλης.
 */
const DESCRIPTION_WORDS = 60;

// Ο πίνακας Kanban των καθηκόντων (ΛΑ-19).
//
// **Δεν είναι αισθητική επιλογή.** Το §4.3.4 ονομάζει **ρητά** τη λογική Kanban,
// με παραπομπή στο §2.7· η υλοποίηση του P1-4 έδωσε κατάλογο. Άρα οι τρεις
// καταστάσεις υπήρχαν χωρίς την **οπτική διαχείριση** που περιγράφει το κείμενο,
// δηλαδή η διαφορά ήταν απόκλιση από την προδιαγραφή και όχι ατέλεια εμφάνισης.
//
// **Οι στήλες διαβάζονται, δεν απαριθμούνται.** Είναι ο κατάλογος καταστάσεων
// του μοντέλου, και η ταύτιση των δύο αντιγράφων του —σύνολο **και σειρά**—
// φυλάσσεται από δοκιμή που διαβάζει και τα δύο αρχεία. Η σειρά δεν είναι
// λεπτομέρεια εδώ: είναι η ροή εργασίας «προς εκτέλεση → σε εξέλιξη →
// ολοκληρωμένο», και ανακατεμένη θα ήταν εξίσου πράσινη σε έλεγχο συνόλων.
//
// ⚠ **ΔΥΟ ΣΚΑΝΔΑΛΕΣ, ΜΙΑ ΣΥΝΑΡΤΗΣΗ — και ποια από τις δύο είναι η κύρια.**
//
// Η **μεταφορά με το ποντίκι είναι συντόμευση**, όχι ο μηχανισμός. Ο κύριος
// δρόμος είναι ο **επιλογέας κατάστασης** κάθε κάρτας, και ο λόγος δεν είναι
// προτίμηση: η μεταφορά της γλώσσας του περιηγητή (*HTML5 drag and drop*) είναι
// **εξ ορισμού απρόσιτη από πληκτρολόγιο** και **σιωπηλή στην αφή** — δεν
// παράγει κανένα συμβάν σε οθόνη αφής. Πίνακας που αλλάζει κατάσταση μόνο με
// σύρσιμο θα έκανε τη ΛΑ-19 ανεκτέλεστη για όποιον δεν χειρίζεται ποντίκι, και
// θα έσπαγε τη ΜΛΑ-11.
//
// Και οι δύο σκανδάλες καλούν την **ίδια** `onChangeStatus`. Δύο διαδρομές που
// γράφουν το ίδιο πράγμα και δεν το κάνουν όμοια αποκλίνουν στη μία τιμή που η
// μία από τις δύο θυμήθηκε.
//
// **Καμία εξάρτηση δεν προστέθηκε.** Η βιβλιοθήκη διεπαφής δεν έχει μεταφορά,
// και η υποδομή μεταφοράς που ήδη υπάρχει στο έργο δένει με δικά της συστατικά
// καταλόγου — θα αντικαθιστούσε τη σήμανση των καρτών με δεύτερο σύστημα λιστών
// δίπλα στο υπάρχον. Χρησιμοποιείται ο μηχανισμός του ίδιου του περιηγητή.

/** Το αναγνωριστικό που ταξιδεύει στη μεταφορά. Ένα, και πάντοτε κείμενο. */
const DRAG_TYPE = 'text/plain';

/**
 * Η προθεσμία, με ρητή ένδειξη όταν λείπει και όταν έχει περάσει (ΛΑ-19).
 *
 * ⚠ **Το χρώμα δεν είναι η πληροφορία, είναι η επιτάχυνσή της.** Το φόντο —κόκκινο
 * για την περασμένη, πράσινο για τη μελλοντική— κάνει την προθεσμία ορατή με μια
 * ματιά πάνω από δώδεκα κάρτες. Δεν αντικαθιστά όμως τη λέξη: η εκφώνηση λέει
 * «Έληξε στις …» ή «Προθεσμία: …» ολόκληρη, γιατί «κόκκινο» δεν είναι πληροφορία
 * για όποιον δεν το βλέπει και για όποιον δεν ξεχωρίζει τις δύο αποχρώσεις
 * (ΜΛΑ-11). Οι δύο συνδυασμοί είναι σημασιολογικές σταθερές, οπότε η αντίθεσή
 * τους μετριέται ήδη κατά την παραγωγή τους.
 *
 * **Το «ολοκληρωμένο με περασμένη προθεσμία» μένει κόκκινο**, και είναι επιλογή:
 * το γεγονός ότι το καθήκον έκλεισε καθυστερημένα είναι αληθές και χρήσιμο. Η
 * κάρτα το ξεχωρίζει ούτως ή άλλως, γιατί πρασινίζει ολόκληρη.
 */
/**
 * Η προθεσμία, σε **σταθερή ελληνική μορφή ηη/μμ/εεεε**.
 *
 * ⚠ **Η μορφή γράφεται ΡΗΤΑ και δεν αφήνεται στον περιηγητή.** Το
 * `toLocaleDateString('el-GR')` δίνει `30/8/2026` σε κάποιες μηχανές και
 * `30/08/2026` σε άλλες — μονοψήφια ή διψήφια ημέρα ανάλογα με την υλοποίηση
 * της βιβλιοθήκης της συσκευής. Σε στοίχιση καρτών αυτό σημαίνει ημερομηνίες
 * **διαφορετικού πλάτους** στην ίδια στήλη, και ο χρήστης δεν έχει τρόπο να
 * ξέρει γιατί. Η ίδια μορφή είναι εξαγόμενη, ώστε ο διάλογος να μη γράψει δεύτερη.
 */
export function formatDueDate(value) {
    return new Intl.DateTimeFormat('el-GR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(value));
}

/**
 * **Πέρασε η προθεσμία;** — σε ένα σημείο, γιατί τη ρωτούν η κάρτα και ο διάλογος.
 */
export const isOverdue = (value) => Boolean(value) && new Date(value).getTime() < Date.now();

/**
 * Η ένδειξη προθεσμίας.
 *
 * Το `inline` δίνει τη μορφή **χωρίς πλαίσιο**, για τη γραμμή στοιχείων της
 * κάρτας: εκεί κάθεται δίπλα στην κατάσταση και στα σχόλια, και τρία πλαίσια
 * στη σειρά είναι τρία κουτιά που δεν σημαίνουν τίποτα. Το **χρώμα μένει**, γιατί
 * αυτό είναι η πληροφορία — και η εκφώνηση κρατά τη **λέξη**, γιατί το χρώμα δεν
 * υπάρχει για όποιον δεν το βλέπει (ΜΛΑ-11).
 */
export function DueBadge(props) {
    return <DueDate {...props} />;
}

function DueDate({ value, inline = false }) {
    if (!value) {
        return (
            <span className={inline
                ? 'inline-flex items-center gap-1.5 text-sm text-muted'
                : 'inline-flex w-fit items-center gap-1.5 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-muted'}
            >
                <i className="fa-regular fa-calendar" aria-hidden="true" />
                {TASKS.noDueDate}
            </span>
        );
    }

    const overdue = isOverdue(value);
    const formatted = formatDueDate(value);

    return (
        <span
            aria-label={overdue ? TASKS.overdueOn(formatted) : TASKS.dueOn(formatted)}
            className={[
                'inline-flex items-center gap-1.5 text-sm font-medium',
                inline ? '' : 'w-fit rounded-lg px-2 py-1',
                overdue ? 'text-danger-soft-foreground' : 'text-success-soft-foreground',
                inline ? '' : (overdue ? 'bg-danger-soft' : 'bg-success-soft')
            ].filter(Boolean).join(' ')}
        >
            <i
                className={overdue ? 'fa-solid fa-triangle-exclamation' : 'fa-regular fa-calendar'}
                aria-hidden="true"
            />
            <span aria-hidden="true">{overdue ? `${TASKS.overdue}: ${formatted}` : formatted}</span>
        </span>
    );
}

/**
 * Οι ετικέτες της κάρτας: κατάσταση, ανάδοχοι, σχετικός πόρος.
 *
 * ⚠ **Η ετικέτα κατάστασης επαναλαμβάνει την επικεφαλίδα της στήλης, και αυτό
 * είναι σκόπιμο.** Σε πίνακα Kanban η στήλη *είναι* η κατάσταση, οπότε η ετικέτα
 * φαίνεται περιττή. Δεν είναι, για δύο λόγους που δεν ισχύουν για την
 * επικεφαλίδα:
 *
 * 1. **Η κάρτα διαβάζεται εκτός στήλης.** Ο αναγνώστης οθόνης που φτάνει στην
 *    κάρτα με `Tab` δεν ξαναδιαβάζει την ενότητα που την περιέχει· χωρίς την
 *    ετικέτα, η κατάσταση δεν εκφωνείται πουθενά.
 * 2. **Η στήλη είναι η υπόθεση της διάταξης· η ετικέτα είναι η αλήθεια της
 *    κάρτας.** Όταν μια αλλαγή απορριφθεί από τον εξυπηρετητή, η κάρτα μένει
 *    εκεί που ήταν και το μήνυμα εξηγεί γιατί — αν η μόνη ένδειξη κατάστασης
 *    ήταν η θέση, η απόρριψη θα φαινόταν ως «δεν έγινε τίποτα».
 *
 * Ο **επιλογέας** παρακάτω δεν την αντικαθιστά: εκείνος είναι *ενέργεια* και
 * υπάρχει μόνο όπου υπάρχει δικαίωμα. Η *πληροφορία* δεν εξαρτάται από
 * δικαίωμα, γι' αυτό η ετικέτα είναι σε **κάθε** κάρτα, με ή χωρίς επιλογέα —
 * αυτό ήταν και το εύρημα Β17, όπου δύο κάρτες της ίδιας στήλης έδειχναν
 * διαφορετικά πράγματα ανάλογα με το ποιος κοιτούσε.
 *
 * **ΣΕΤ Δ:** οι ανάδοχοι έφυγαν από εδώ και έγιναν **πρόσωπα** στην τελευταία
 * γραμμή, και ο πόρος ανέβηκε **πάνω από τον τίτλο**. Ο λόγος είναι ότι οι τρεις
 * πληροφορίες δεν είναι ομοειδείς: ο πόρος είναι το **πλαίσιο** («για ποιο
 * πράγμα»), οι ανάδοχοι είναι **πρόσωπα**, και μόνο η κατάσταση είναι ιδιότητα
 * του ίδιου του καθηκόντος. Στοιβαγμένες ως ισότιμες ετικέτες, μια κάρτα με
 * τρεις αναδόχους και μακρύ τίτλο πόρου γινόταν τέσσερις σειρές θορύβου πάνω
 * από τη μία σειρά που ενδιέφερε.
 */
/*
 * ⚠ **ΕΔΩ ΖΟΥΣΕ Η ΕΤΙΚΕΤΑ ΚΑΤΑΣΤΑΣΗΣ ΤΗΣ ΚΑΡΤΑΣ, ΚΑΙ ΑΦΑΙΡΕΘΗΚΕ.**
 *
 * Το επιχείρημα που την είχε βάλει (**Β17**) ήταν ότι **η πληροφορία δεν
 * εξαρτάται από δικαίωμα**: η κάρτα χωρίς επιλογέα έπρεπε να λέει κι αυτή σε
 * ποια κατάσταση βρίσκεται. Το επιχείρημα **στέκει και τηρείται** — απλώς δεν το
 * υπηρετεί πλέον chip.
 *
 * Η κατάσταση ζει τώρα στη **γραμμή στοιχείων**, με εικονίδιο και λέξη, σε κάθε
 * κάρτα: ως **επιλογέας** όπου υπάρχει δικαίωμα, ως **κείμενο** όπου δεν
 * υπάρχει. Δύο αναπαραστάσεις της ίδιας κατάστασης στην ίδια κάρτα —chip πάνω,
 * επιλογέας κάτω— ήταν πλεονασμός· και το chip της ολοκλήρωσης ήταν το **τρίτο**
 * πράσινο της κάρτας, μαζί με το φόντο και τη μελλοντική προθεσμία.
 */

/**
 * Ο συνδεδεμένος πόρος, **πάνω από τον τίτλο** και όχι κάτω από αυτόν.
 *
 * Είναι το πλαίσιο μέσα στο οποίο διαβάζεται ο τίτλος: «Έλεγχος επιλεξιμότητας»
 * σημαίνει άλλο πράγμα κάτω από κάθε πρόσκληση. Το πλαίσιο που έρχεται **μετά**
 * υποχρεώνει τον αναγνώστη να ξαναδιαβάσει τον τίτλο.
 */
function ResourceChip({ resource }) {
    if (!resource?.title) return null;

    return (
        <StatusChip
            icon="fa-solid fa-link"
            tone="neutral"
            appearance="outline"
            title={`${TASKS.resource}: ${resource.title}`}
        >
            {/* ⚠ **ΧΩΡΙΣ `truncate`.** Το κόψιμο έλυνε το πλάτος και **έσπαγε
                την πληροφορία**: οι τίτλοι των προσκλήσεων μοιάζουν στην αρχή
                τους («Πρόσκληση υποβολής προτάσεων…»), οπότε τρεις κάρτες
                έδειχναν τρεις φορές το ίδιο κείμενο και τίποτα που να τις
                ξεχωρίζει. Η ετικέτα τυλίγεται σε όσες γραμμές χρειάζεται. */}
            <span className="block max-w-full">{resource.title}</span>
        </StatusChip>
    );
}

/**
 * Η γραμμή των προσώπων: πλήθος σημειώσεων αριστερά, ανάδοχοι δεξιά.
 *
 * Το **πλήθος** των σημειώσεων ταξιδεύει ήδη ολόκληρο στον κατάλογο, οπότε δεν
 * χρειάστηκε ούτε πεδίο ούτε δεύτερο αίτημα. Εμφανίζεται **μόνο όταν υπάρχει**:
 * ένα «0» δίπλα σε εικονίδιο σχολίων είναι θόρυβος σε κάθε νέα κάρτα.
 */
/**
 * **Η ΓΡΑΜΜΗ ΣΤΟΙΧΕΙΩΝ ΤΗΣ ΚΑΡΤΑΣ: ΠΡΟΘΕΣΜΙΑ ΚΑΙ ΣΧΟΛΙΑ.**
 *
 * Ζουν μαζί με τον επιλογέα κατάστασης, στην ίδια χαμηλή γραμμή, γιατί είναι το
 * ίδιο είδος πράγματος: **σταθερά στοιχεία του καθηκόντος**, όχι περιεχόμενο.
 * Πριν, η προθεσμία ήταν χρωματιστό πλαίσιο **μέσα** στο κουμπί του τίτλου και ο
 * αριθμός σχολίων μια γραμμή πιο κάτω, σε μικρότερη γραμματοσειρά — τρία μεγέθη
 * και τρεις θέσεις για πληροφορία που διαβάζεται μαζί.
 *
 * **Το ίδιο μέγεθος και για τα δύο**, γιατί έχουν την ίδια βαρύτητα. Ο αριθμός
 * σχολίων **δεν αποδίδεται στο μηδέν**: ένα «0» δίπλα σε εικονίδιο σχολίων είναι
 * θόρυβος σε κάθε νέα κάρτα.
 */
function CardMeta({ task }) {
    const notes = task.notes?.length ?? 0;

    return (
        <span className="ms-auto flex items-center gap-3">
            <DueDate value={task.dueDate} inline />
            {notes > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-muted" aria-label={TASKS.notesCountOf(notes)}>
                    <i className="fa-regular fa-comment" aria-hidden="true" />
                    <span aria-hidden="true">{notes}</span>
                </span>
            )}
        </span>
    );
}

/**
 * Τα **πρόσωπα** της κάρτας.
 *
 * Το πλήθος σημειώσεων έφυγε από εδώ και πήγε στη γραμμή στοιχείων: ήταν σε
 * μικρότερη γραμματοσειρά από την προθεσμία, δηλαδή δύο μεγέθη για πληροφορία
 * που ο χρήστης διαβάζει με μία ματιά.
 */
function TaskFooter({ task }) {
    const assignees = task.assignees ?? [];

    return (
        <span className="flex items-center justify-end gap-2">
            {assignees.length === 0 ? (
                <span className="text-xs text-muted">{TASKS.noAssignees}</span>
            ) : (
                // ⚠ **ΤΟ ΜΕΓΕΘΟΣ ΞΑΝΑΓΥΡΙΣΕ ΣΤΟ ΠΡΟΕΠΙΛΕΓΜΕΝΟ.** Η σμίκρυνση
                // είχε επιχείρημα —ο ανάδοχος είναι δευτερεύουσα πληροφορία στην
                // κάρτα— και **δεν άντεξε την οθόνη**: στα 20 πίξελ τα αρχικά
                // του ονόματος έπαυαν να διαβάζονται, οπότε το πρόσωπο γινόταν
                // κουκκίδα που δεν ταυτοποιεί κανέναν.
                //
                // *Πληροφορία που σμικρύνεται μέχρι να μη διαβάζεται δεν έγινε
                // δευτερεύουσα· έγινε θόρυβος.* Η ιεράρχηση γίνεται με τη **θέση**
                // —τα πρόσωπα είναι τελευταία στην κάρτα— και όχι με το μέγεθος.
                //
                // Το `sizeClass` του `AvatarStack` **μένει**: είναι σωστή
                // παράμετρος και δεν κοστίζει τίποτα αχρησιμοποίητη.
                <AvatarStack users={assignees} label={TASKS.assigneeOf} />
            )}
        </span>
    );
}

/**
 * Μία κάρτα.
 *
 * Χωριστό συστατικό ώστε οι χειριστές της να είναι **ονομασμένες συναρτήσεις με
 * το καθήκον στο περιβάλλον τους**. Ενσωματωμένα βέλη μέσα σε χαρακτηριστικά JSX
 * θα έσπαγαν τον στατικό έλεγχο καλωδίωσης, που διαβάζει τα χαρακτηριστικά ως
 * κείμενο μέχρι το πρώτο `>` — και το βέλος περιέχει ένα.
 */
function TaskCard({ task, canProgress, collectionName, onOpenTask, onChangeStatus }) {
    function handleOpen() {
        onOpenTask(task);
    }

    function handleStatus(key) {
        const next = String(key);
        if (next === task.status) return;
        onChangeStatus(task, next);
    }

    function handleDragStart(event) {
        event.dataTransfer.setData(DRAG_TYPE, task.id);
        event.dataTransfer.effectAllowed = 'move';

        // ⚠ **Η ΕΙΚΟΝΑ ΤΗΣ ΜΕΤΑΦΟΡΑΣ ΕΙΝΑΙ ΑΝΤΙΓΡΑΦΟ ΠΟΥ ΦΤΙΑΧΝΟΥΜΕ ΕΜΕΙΣ.**
        //
        // **Δεύτερη διόρθωση, και η πρώτη δεν ήταν λάθος — ήταν ανεπαρκής.**
        // Εκείνη έδινε στον περιηγητή το **ζωντανό** στοιχείο της κάρτας
        // (`setDragImage(event.currentTarget, …)`), δηλαδή του ζητούσε να
        // φωτογραφίσει έναν κόμβο **μέσα στο δέντρο**. Το αποτέλεσμα εξαρτάται
        // από πράγματα που η κάρτα δεν ελέγχει: αν η ίδια ή κάποιος πρόγονός της
        // συμμετέχει σε **σύνθετο επίπεδο** —και η κλάση `.card` δηλώνει
        // `transition: transform`, που αρκεί σε ορισμένες μηχανές— η φωτογραφία
        // βγαίνει του **επιπέδου** και όχι του στοιχείου. Τότε σύρεται ό,τι
        // βρίσκεται σε εκείνο το επίπεδο, δηλαδή μεγάλο μέρος της σελίδας.
        //
        // Εδώ ο περιηγητής δεν φωτογραφίζει τίποτα δικό μας: παίρνει **έτοιμο
        // αντίγραφο**, εκτός οθόνης, χωρίς προγόνους, χωρίς μεταβάσεις και με
        // ρητό πλάτος. Το ερώτημα «σε ποιο επίπεδο ζει;» παύει να τίθεται.
        //
        // **Το αντίγραφο ζει λιγότερο από ένα καρέ.** Ο περιηγητής το διαβάζει
        // **σύγχρονα** μέσα στο `setDragImage`, οπότε η αφαίρεσή του στο επόμενο
        // βήμα του βρόχου συμβάντων είναι ασφαλής — και υποχρεωτική, αλλιώς
        // κάθε σύρσιμο αφήνει έναν κόμβο πίσω του.
        const card = event.currentTarget;
        if (typeof event.dataTransfer.setDragImage !== 'function') return;

        const box = card.getBoundingClientRect();
        const ghost = card.cloneNode(true);

        ghost.style.position = 'fixed';
        // Εκτός οθόνης, **όχι** `display:none` ούτε `visibility:hidden`: κρυφό
        // στοιχείο δεν φωτογραφίζεται καθόλου και ο περιηγητής πέφτει σιωπηλά
        // πίσω στην προεπιλογή — δηλαδή στο σφάλμα που διορθώνουμε.
        ghost.style.top = '-10000px';
        ghost.style.left = '-10000px';
        ghost.style.width = `${box.width}px`;
        ghost.style.pointerEvents = 'none';
        ghost.style.transition = 'none';
        ghost.style.transform = 'none';
        ghost.style.margin = '0';

        document.body.appendChild(ghost);
        event.dataTransfer.setDragImage(ghost, event.clientX - box.left, event.clientY - box.top);
        setTimeout(() => ghost.remove(), 0);
    }

    // ⚠ **Η ΚΑΡΤΑ ΜΕΝΕΙ ΛΕΥΚΗ· ΠΡΑΣΙΝΙΖΕΙ Η ΣΤΗΛΗ.**
    //
    // Η προηγούμενη μορφή έβαφε πράσινη την ολοκληρωμένη κάρτα, με επιχείρημα
    // που **στέκει**: η κάρτα διαβάζεται και εκτός στήλης. Το τίμημα όμως ήταν
    // ότι στην ίδια κάρτα συνυπήρχαν **τρία** πράσινα — φόντο, ετικέτα
    // κατάστασης, και η μελλοντική προθεσμία — που σημαίνουν διαφορετικά
    // πράγματα. Τρία σήματα του ίδιου χρώματος δεν είναι έμφαση: είναι θόρυβος,
    // και το χρώμα παύει να λέει οτιδήποτε.
    //
    // Η πληροφορία **δεν χάθηκε**: την κρατά η ετικέτα κατάστασης, που είναι σε
    // κάθε κάρτα και ταξιδεύει μαζί της. Το φόντο μετακόμισε στη **στήλη**, όπου
    // υπάρχει μία φορά αντί για μία ανά κάρτα.
    return (
        <li
            draggable={canProgress}
            onDragStart={handleDragStart}
            className={[
                'flex flex-col gap-2 rounded-xl border border-border bg-surface p-3',
                canProgress ? 'cursor-grab select-none active:cursor-grabbing' : ''
            ].join(' ')}
        >
            {/* α. Το πλαίσιο, πριν από τον τίτλο. */}
            <ResourceChip resource={task.resource ?? null} />

            <button
                type="button"
                onClick={handleOpen}
                aria-label={TASKS.boardOpenOf(task.title)}
                className="flex flex-col items-start gap-2 rounded-lg text-left hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            >
                {/* β. Ο τίτλος. **Μεγαλύτερος από τα υπόλοιπα**, γιατί είναι
                    το μόνο πράγμα που ξεχωρίζει τη μία κάρτα από την άλλη: όλα
                    τα υπόλοιπα —ετικέτες, πρόσωπα, ημερομηνίες— επαναλαμβάνονται
                    ανάμεσα στις κάρτες. Με το ίδιο μέγεθος με τις ετικέτες του,
                    το μάτι δεν είχε πού να προσγειωθεί. */}
                <span className="text-base leading-snug font-semibold">{task.title}</span>
                {collectionName && (
                    <span className="text-xs text-accent">{`${TASKS.inCollection}: ${collectionName}`}</span>
                )}

                {/* γ. **Η ΑΡΧΗ ΤΗΣ ΠΕΡΙΓΡΑΦΗΣ.** Ο τίτλος ενός καθηκόντος είναι
                    συχνά τρεις λέξεις —«Έλεγχος επιλεξιμότητας»— και δύο κάρτες
                    με τον ίδιο τίτλο κάτω από διαφορετικές προσκλήσεις ήταν
                    δυσδιάκριτες χωρίς άνοιγμα. Οι πρώτες λέξεις της περιγραφής
                    λύνουν ακριβώς αυτό.

                    **Κόβεται σε λέξεις και ΚΑΙ σε γραμμές**, από την ίδια
                    συνάρτηση που κόβει την περιγραφή της συλλογής: το όριο
                    λέξεων κρατά πεπερασμένο ό,τι ταξιδεύει, το `line-clamp`
                    κρατά το ύψος. Καθήκον χωρίς περιγραφή **δεν αφήνει κενό**:
                    εδώ, σε αντίθεση με τις κάρτες συλλογών, οι κάρτες δεν
                    στοιχίζονται μεταξύ τους — ζουν σε στήλη, η μία κάτω από την
                    άλλη. */}
                {task.description && (
                    // **Σε συννεφάκι**, όπως η περιγραφή του πόρου και του
                    // καθηκόντος στον διάλογο. Το πλαίσιο δεν είναι διακόσμηση:
                    // είναι το **όριο** που λέει πού τελειώνει το ελεύθερο
                    // κείμενο και πού αρχίζουν τα στοιχεία της κάρτας — και εδώ
                    // η κάρτα έχει τέσσερα διαφορετικά πράγματα στη σειρά.
                    <span className="line-clamp-3 rounded-lg border border-border bg-background p-2 text-sm font-normal text-muted">
                        {firstWords(task.description, DESCRIPTION_WORDS)}
                    </span>
                )}
            </button>

            {/* δ. Σημειώσεις και πρόσωπα. */}
            <TaskFooter task={task} />


            {/* **Ο κύριος δρόμος αλλαγής κατάστασης.** Η μεταφορά είναι
                συντόμευση για το ποντίκι· αυτό εδώ φτάνεται με `Tab` και
                διαβάζεται από αναγνώστη οθόνης.

                Απουσιάζει —και δεν αντικαθίσταται από ανενεργό πεδίο— όπου δεν
                υπάρχει δικαίωμα. Η **κατάσταση** φαίνεται ήδη στην ετικέτα της
                κάρτας, σε κάθε κάρτα· εδώ λείπει μόνο η **ενέργεια**, που είναι
                ακριβώς αυτό που λείπει στην πραγματικότητα. */}
            {canProgress && (
                /* ⚠ **Ο ΕΠΙΛΟΓΕΑΣ ΔΕΝ ΕΙΝΑΙ ΠΕΔΙΟ ΦΟΡΜΑΣ ΚΑΙ ΕΠΑΨΕ ΝΑ ΤΟ ΔΕΙΧΝΕΙ.**
                    Στην προηγούμενη μορφή ήταν πλήρους πλάτους, με την όψη
                    πεδίου εισόδου, και **κυριαρχούσε στην κάρτα**: το μεγαλύτερο
                    στοιχείο ήταν το χειριστήριο και όχι το περιεχόμενο.

                    Πλέον κάθεται σε **δική του γραμμή με διαχωριστικό**, με το
                    εικονίδιο της τρέχουσας κατάστασης μπροστά — **το ίδιο**
                    εικονίδιο με την ετικέτα, από τον κοινό κατάλογο. Ο χρήστης
                    βλέπει τι είναι **και** τι μπορεί να αλλάξει, στο ίδιο
                    σύμβολο.

                    Το `w-auto` και η στοίχιση στην αρχή: το χειριστήριο παίρνει
                    το πλάτος του κειμένου του, όχι της κάρτας. */
                <div className="mt-1 flex items-center gap-2 border-t border-border pt-2">
                    <i
                        className={TASK_STATUS_APPEARANCE[task.status]?.icon ?? TASK_STATUS_APPEARANCE.todo.icon}
                        aria-hidden="true"
                    />
                    <Select placeholder={SELECT_PLACEHOLDER}
                        selectedKey={task.status}
                        onSelectionChange={handleStatus}
                        aria-label={TASKS.boardStatusOf(task.title)}
                        className="w-auto min-w-0"
                    >
                        {/* ⚠ **ΧΩΡΙΣ `Select.Indicator`.** Το βελάκι της βιβλιοθήκης
                            είναι σωστό όσο η σκανδάλη μοιάζει με πεδίο — έχει
                            περίγραμμα και φόντο, και το βελάκι λέει «αυτό ανοίγει».
                            Εδώ το περίγραμμα αφαιρέθηκε επίτηδες, οπότε το βελάκι
                            έμενε ένα **αιωρούμενο σχήμα** δίπλα σε κείμενο, χωρίς
                            τίποτα να το εξηγεί.

                            Ό,τι λέει το βελάκι το λέει ήδη το **εικονίδιο
                            κατάστασης** από αριστερά, και η ίδια η σκανδάλη
                            παραμένει πλήρες χειριστήριο για το πληκτρολόγιο και
                            τον αναγνώστη οθόνης: αφαιρέθηκε **διακοσμητικό**, όχι
                            πληροφορία. */}
                        <Select.Trigger className="flex items-center gap-2 border-0 bg-transparent px-1 font-medium shadow-none">
                            <Select.Value />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                {TASK_STATUS_KEYS.map((key) => (
                                    <ListBoxItem key={key} id={key}>{TASKS.status[key]}</ListBoxItem>
                                ))}
                            </ListBox>
                        </Select.Popover>
                    </Select>

                    <CardMeta task={task} />
                </div>
            )}

            {/* Χωρίς δικαίωμα προόδου δεν υπάρχει επιλογέας, αλλά **η γραμμή
                μένει**: η ημερομηνία και τα σχόλια είναι πληροφορία και δεν
                εξαρτώνται από δικαίωμα. Είναι ο ίδιος κανόνας που κρατά την
                ετικέτα κατάστασης σε κάθε κάρτα. */}
            {!canProgress && (
                <div className="mt-1 flex items-center gap-2 border-t border-border pt-2">
                    {/* ⚠ **Η ΚΑΤΑΣΤΑΣΗ ΩΣ ΚΕΙΜΕΝΟ, ΚΑΙ ΕΙΝΑΙ ΤΟ ΕΥΡΗΜΑ Β17 ΣΕ ΝΕΑ
                        ΜΟΡΦΗ.** Η ετικέτα λέει *τι είναι*, ο επιλογέας *τι μπορείς
                        να κάνεις*: η πληροφορία δεν εξαρτάται από δικαίωμα, η
                        ενέργεια εξαρτάται. Χωρίς αυτό, η κάρτα του τρίτου θα ήταν
                        η μόνη που δεν λέει σε ποια κατάσταση βρίσκεται — και θα
                        διαβαζόταν εκτός στήλης, όπου η θέση δεν είναι ένδειξη.

                        **Ίδιο εικονίδιο και ίδιο μέγεθος** με του επιλογέα: ο ίδιος
                        χρήστης βλέπει και τις δύο μορφές δίπλα-δίπλα, σε γειτονικές
                        κάρτες. */}
                    <i
                        className={TASK_STATUS_APPEARANCE[task.status]?.icon ?? TASK_STATUS_APPEARANCE.todo.icon}
                        aria-hidden="true"
                    />
                    <span className="text-sm font-medium">{TASKS.status[task.status] ?? TASKS.status.todo}</span>

                    <CardMeta task={task} />
                </div>
            )}
        </li>
    );
}

export default function TaskBoard({
    tasks = [],
    currentUserId,
    canManageOf,
    onOpenTask,
    onChangeStatus,
    collectionNameOf = null,
    isReadOnly = false
}) {
    const [dragOver, setDragOver] = useState('');
    const [error, setError] = useState('');
    const [mineOnly, setMineOnly] = useState(false);

    /**
     * Η **μία** απόφαση δικαιώματος, από τον κοινό κανόνα.
     *
     * Δεν ξαναγράφεται εδώ: ο κανόνας «ο ανάδοχος ή η διαχείριση» ζει σε ένα
     * σημείο και τον καταναλώνει και ο διάλογος. Ο πραγματικός φράχτης είναι ο
     * εξυπηρετητής — εδώ κρίνεται μόνο τι εμφανίζεται.
     */
    function canProgressOn(task) {
        if (isReadOnly) return false;
        return mayProgress(task, {
            userId: currentUserId,
            canManage: canManageOf ? canManageOf(task) === true : false
        });
    }

    /**
     * Ο χειριστής απόρριψης, ως **ονομασμένη** συνάρτηση και όχι ενσωματωμένο
     * βέλος: ένα `onDismiss={() => …}` περιέχει `>` και κόβει τη σάρωση του
     * στατικού ελέγχου καλωδίωσης στη μέση της ετικέτας.
     */
    function dismissError() {
        setError('');
    }

    function change(task, status) {
        setError('');
        const outcome = onChangeStatus(task, status);
        if (outcome && typeof outcome.catch === 'function') {
            outcome.catch((reason) => setError(reason?.message ?? TASKS.boardChangeFailed));
        }
    }

    function handleDragOver(event) {
        // Χωρίς `preventDefault` ο περιηγητής **αρνείται την απόθεση** και το
        // συμβάν `drop` δεν φτάνει ποτέ: η κάρτα επιστρέφει στη θέση της και
        // τίποτα δεν λέει γιατί.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOver(event.currentTarget.dataset.status ?? '');
    }

    function handleDragLeave() {
        setDragOver('');
    }

    function handleDrop(event) {
        event.preventDefault();
        setDragOver('');

        const status = event.currentTarget.dataset.status ?? '';
        const taskId = event.dataTransfer.getData(DRAG_TYPE);
        const task = tasks.find((candidate) => candidate.id === taskId);

        if (!task || !status || task.status === status) return;
        if (!canProgressOn(task)) return;

        change(task, status);
    }

    /**
     * ⚠ **ΤΟ ΦΙΛΤΡΟ ΖΕΙ ΣΤΟΝ ΠΙΝΑΚΑ, ΟΧΙ ΣΕ ΚΑΘΕ ΟΘΟΝΗ ΠΟΥ ΤΟΝ ΧΡΗΣΙΜΟΠΟΙΕΙ.**
     *
     * Τον πίνακα τον μοιράζονται η καρτέλα της συλλογής και η **διασυλλογική**
     * οθόνη. Δύο υλοποιήσεις του ίδιου φίλτρου θα απέκλιναν στον ορισμό του
     * «δικό μου» — και ο ορισμός δεν είναι προφανής: ο ανάδοχος έρχεται άλλοτε
     * ως αναγνωριστικό και άλλοτε ως αντικείμενο.
     *
     * **Φιλτράρει στον πελάτη, και είναι σωστό εδώ**, σε αντίθεση με τα φίλτρα
     * των πόρων που ζουν στον διακομιστή: ο πίνακας έχει **ήδη ολόκληρο** τον
     * κατάλογο στη μνήμη —δεν σελιδοποιείται— οπότε δεν υπάρχει «η επόμενη
     * σελίδα» που θα έμενε αφιλτράριστη. Το επιχείρημα της ΛΑ-15 δεν ισχύει σε
     * κατάλογο χωρίς σελίδες.
     */
    const visible = mineOnly
        ? tasks.filter((task) => isAssignee(task, currentUserId))
        : tasks;

    const byStatus = Object.fromEntries(
        TASK_STATUS_KEYS.map((key) => [key, visible.filter((task) => task.status === key)])
    );

    const mineCount = tasks.filter((task) => isAssignee(task, currentUserId)).length;

    function toggleMine() {
        setMineOnly((current) => !current);
    }

    return (
        <div className="flex flex-col gap-3">
            {/* **Το μήνυμα ΔΙΠΛΑ στην ενέργεια**, μέσα στον πίνακα και ποτέ στην
                κορυφή της σελίδας: ο πίνακας μπορεί να έχει δική του κύλιση, και
                σφάλμα που εμφανίζεται εκτός οπτικού πεδίου είναι σφάλμα που ο
                χρήστης δεν βλέπει — η κάρτα απλώς γυρίζει στη θέση της. */}
            {error && (
                <Notice status="warning" title={TASKS.boardChangeFailed} onDismiss={dismissError}>
                    {error}
                </Notice>
            )}

            {/* ⚠ **ΤΟ ΚΟΥΜΠΙ ΛΕΕΙ ΤΟ ΠΛΗΘΟΣ, ΚΑΙ ΔΕΝ ΕΙΝΑΙ ΔΙΑΚΟΣΜΗΣΗ.** Φίλτρο
                που δείχνει «0 αποτελέσματα» **αφού** πατηθεί είναι δρόμος χωρίς
                έξοδο· με τον αριθμό πάνω του, ο χρήστης ξέρει **πριν** πατήσει
                ότι δεν του έχει ανατεθεί τίποτα.

                **Δεν αποδίδεται καθόλου όταν δεν υπάρχει ταυτότητα χρήστη**: το
                «τα δικά μου» χωρίς «εμένα» δεν είναι ερώτημα.

                Το `aria-pressed` είναι η σωστή σήμανση για **διακόπτη** και όχι
                για ενέργεια: ο αναγνώστης οθόνης εκφωνεί «πατημένο», δηλαδή τη
                **δίκη του κατάσταση**. Ένα απλό κουμπί θα άφηνε τον χρήστη να
                μαντεύει αν το φίλτρο είναι ενεργό — και το ενεργό φίλτρο είναι
                ακριβώς ο λόγος που ο κατάλογος φαίνεται μικρός (ΜΛΑ-03). */}
            {currentUserId && (
                <Button
                    size="sm"
                    variant={mineOnly ? 'solid' : 'outline'}
                    onPress={toggleMine}
                    aria-pressed={mineOnly}
                    className="self-start"
                >
                    <i className="fa-solid fa-user-check" aria-hidden="true" />
                    {TASKS.mineOnly}
                    <span className="ms-1 text-xs opacity-80">{`(${mineCount})`}</span>
                </Button>
            )}

            {/* Όταν το φίλτρο κρύβει τα πάντα, ο πίνακας **λέει γιατί**: τρεις
                κενές στήλες δίπλα σε πατημένο κουμπί διαβάζονται ως «η συλλογή
                δεν έχει καθήκοντα», που είναι ψέμα. */}
            {mineOnly && mineCount === 0 && (
                <p className="text-sm text-muted">{TASKS.mineOnlyEmpty}</p>
            )}

            <div className="grid gap-3 lg:grid-cols-3">
                {TASK_STATUS_KEYS.map((status) => (
                    <section
                        key={status}
                        data-status={status}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        aria-label={TASKS.status[status]}
                        className={[
                            'flex min-h-24 flex-col gap-2 rounded-2xl border p-3 transition-colors',
                            // ⚠ **ΤΟ ΠΡΑΣΙΝΟ ΤΗΣ ΟΛΟΚΛΗΡΩΣΗΣ ΖΕΙ ΕΔΩ, ΜΙΑ ΦΟΡΑ.**
                            // Ήταν στην κάθε κάρτα, δηλαδή τόσες φορές όσες οι
                            // ολοκληρωμένες. Στη στήλη λέει το ίδιο πράγμα και
                            // αφήνει τις κάρτες λευκές, ώστε τα **δικά τους**
                            // χρώματα —η ετικέτα και η προθεσμία— να ξαναγίνουν
                            // ορατά. Η προτεραιότητα του `dragOver` μένει: όσο
                            // σέρνεται κάρτα, η στήλη λέει «εδώ πέφτει», και αυτό
                            // υπερισχύει κάθε άλλης πληροφορίας.
                            dragOver === status
                                ? 'border-accent bg-accent-soft'
                                : status === 'done'
                                    ? 'border-success bg-success-soft'
                                    : 'border-border bg-surface'
                        ].join(' ')}
                    >
                        {/* Το εικονίδιο **πριν** από τον τίτλο, και είναι το ίδιο
                            με της ετικέτας κάθε κάρτας — από τον κοινό κατάλογο,
                            όχι από δεύτερο αντίγραφο εδώ. Είναι `aria-hidden`:
                            η επικεφαλίδα εκφωνείται ήδη ολόκληρη, και το
                            εικονίδιο δεν προσθέτει πληροφορία για όποιον ακούει. */}
                        <h4 className="flex items-center justify-between gap-2 text-sm font-semibold">
                            <span className="flex items-center gap-2">
                                <i
                                    className={TASK_STATUS_APPEARANCE[status]?.icon ?? TASK_STATUS_APPEARANCE.todo.icon}
                                    aria-hidden="true"
                                />
                                {TASKS.status[status]}
                            </span>
                            <span className="text-xs font-normal text-muted">{byStatus[status].length}</span>
                        </h4>

                        {byStatus[status].length === 0 ? (
                            <p className="text-xs text-muted">{TASKS.boardEmptyColumn}</p>
                        ) : (
                            <ul className="flex flex-col gap-2">
                                {byStatus[status].map((task) => (
                                    <TaskCard
                                        key={task.id}
                                        task={task}
                                        canProgress={canProgressOn(task)}
                                        collectionName={collectionNameOf ? collectionNameOf(task) : null}
                                        onOpenTask={onOpenTask}
                                        onChangeStatus={change}
                                    />
                                ))}
                            </ul>
                        )}
                    </section>
                ))}
            </div>

            {!isReadOnly && <p className="text-xs text-muted">{TASKS.boardDragHint}</p>}
        </div>
    );
}
